import { invoiceLineSummary } from '../orders';

export interface Returns {
  id: number;
  documentDate: string;
  customerName: string;
  customerId?: string;
  status: string;
  returnOrderNo: string;
  erpOrderNumber: string;
  returnAmt: string;
  batchCount: number;
  importSource: string;
}

export interface ReturnDrafts {
  id: number;
  documentDate: string;
  customerName: string;
  customerId?: string;
  status: string;
  returnOrderNo: string;
  erpOrderNumber: string;
  returnAmt: string;
  batchCount: number;
  importSource: string;
  templateNotFound: boolean;
  ticketCreated: boolean;
}
export interface ReturnDetailsData {
  data: {
    id: number;
    createOn: string;
    erpOrderNumber: string;
    importSource: string;
    skuCount: number;
    returnAmt: string;
    customerName: string;
    divisions: Division[];
    returns: ReturnDetails[];
    totalRecords: number;
    startRecord: number;
    endRecord: number;
  };
}

interface Division {
  divisionId: string;
  divisionName: string;
}

export interface ReturnDetails {
  batchNumber: string;
  status: string;
  title: string;
  skuCode: string;
  quantity: number;
  expiryDate: string;
  invoiceNumber: string;
  pts: string;
  category: string;
  cfaType: string;
  cfaRemarks: string;
}

export interface GetReturnsRequest {
  body: {
    includeProduct: boolean;
    pageNo: number;
    pageSize: number;
    customerId: string;
    includeSummary?: boolean;
    userType: string;
    filters?: Partial<{
      status?: string[];
      importSources?: string[];
      cfaIds?: string[];
      returnAmountRange?: Partial<{
        minAmount?: number;
        maxAmount?: number;
      }>;
    }>;
  };
  workSpaceId: string;
  hideReturnStatus: boolean;
  dispatchFilters?: boolean;
  params: {
    sellerWorkspaceId: string;
  };
}

export interface BatchSearchRequest {
  sellerWorkspace: string;
  batchCodes: string[];
  includeProduct: boolean;
  includeInvoice: boolean;
  productVariantIds: number[];
  customerId: string;
  doPartialSearch: boolean;
  headDivisionId: string;
  category: string;
  sendErrMsg?: boolean;
}

export interface BatchSearchResponse {
  id: string;
  batchCode: string;
  expiryDate: string;
  manufactureDate?: string;
  packsize?: number;
  divisionId?: string;
  headDivisionId?: string;
  listPrice?: number;
  category: string;
  productVariantId?: number;
  productVariant?: ProductVariant;
  invoiceLines?: InvoiceLine[];
  returnType?: string;
  status?: string;
}

interface InvoiceLine {
  invoiceNumber: string;
  invoiceId?: string;
  quantity?: number;
  listPrice?: number;
  lineTotal?: number;
  lineTotalWithAdjustment?: number;
  lineTotalWithTax?: number;
}

interface ProductVariant {
  id: number;
  sku: string;
  skuName: string;
}

export interface CreateReturnRequest {
  sellerWorkspace: string;
  customerId: string;
  cfaId?: string;
  importSource: string;
  orderPlacedAt: string;
  returnOrderId?: number;
  headDivisionId?: string;
  lines: CreateReturnLine[];
  noOfBoxes?: number;
  goodsReceivedDate?: string;
  vehicleDetails?: string;
  lrNumber?: string;
  lrDate?: string;
  category?: string;
}

export interface CreateReturnData {
  id: number | string;
  returnOrderLineId?: number;
  batches?: BatchSearchResponse[];
  selectedBatch: BatchSearchResponse;
  productVariant: {
    listPrice?: any;
    id: number;
    sku: string;
    name: string;
  };
  erpOrderNumber?: string;
  selectedInvoice?: InvoiceLine;
  quantity: number;
  deleted: boolean;
  returnType?: string;
  status?: string;
  cfaType?: string;
  comments?: Comment[];
  returnOrderCreditNote?: any;
  // for splitting
  isParent?: boolean; // can be removed
  childCount?: number;
  isChild?: boolean;
  isLastChild?: boolean;
  parentIndex?: number;
  childIds?: (number | string)[];
  error?: string;
  edited: boolean;
}

export interface Comment {
  commentId?: string;
  comment: string;
  userId?: string;
  userType?: string;
}

export interface CreateReturnLine {
  returnOrderLineId?: number;
  productVariantId: number;
  sku?: string;
  invoiceId?: string;
  batchId: string;
  batchCode?: string;
  quantity: number;
  invoiceNumber?: string;
  category: string;
  returnType?: string;
  status?: string;
  comments?: Comment[];
}

export interface ReturnOrder {
  id: number;
  documentDate?: string;
  customerId: string;
  status: string;
  createdBy: string;
  createdByName: string;
  userType: string;
  subTotal: number;
  grossTotal: number;
  cfaId: string;
  cfaName: string;
  refOrderNumber: string;
  erpOrderNumber: string;
  custErpRONumber?: string;
  createdAt: string;
  updatedAt: string;
  skucount: number;
  taxAmount: number;
  totalQuantity: number;
  importSource: string;
  lines: ReturnOrderLine[];
  customerName: string;
  headDivisionCode?: string;
  headDivisionId?: string;
  headDivisionName?: string;
  romdUploadStatus?: string;
  freshdeskTicketId?: number | null;
  customerRefInvoiceNumber: string;
}

export interface ReturnOrderLine {
  batchNumber: string;
  batchId: string;
  cfaType: string | null;
  remarks: Comment[];
  expiryDate: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  quantity: number;
  erpOrderNumber: string;
  sku: string;
  productVariant?: ProductVariant;
  unitPrice: string;
  invoiceLineSummary: invoiceLineSummary;
  returnType?: string;
  status?: string;
  returnOrderId: number;
  linePrice: number;
  id: number;
  productVariantId: number;
  invoiceNumber?: string;
  headDivisionCode?: null;
  headDivisionId?: null;
  headDivisionName?: null;
  returnOrderCreditNote?: any;
  errorMsg?: string;
}

interface ProductVariant {
  id: number;
  sku: string;
  listPrice: number;
  name: string;
}

export interface FetchReturnOrdersResponse {
  totalSubmitted: number;
  pendingForApproval: number;
  pendingToRecieve: number;
  creditRecieved: number;
  returnOrders: ReturnOrder[];
  totalRecords: number;
  startRecord: number;
  endRecord: number;
  status: string;
  allStatus: { status: string }[];
  importSources: { importSource: string }[];
  cfaIds: { cfaId: string; cfaName: string | null }[];
}

export interface GetReturnOrderRequest {
  body: {
    returnOrderId: string;
    includeProduct: boolean;
    includeComments: boolean;
  };
  workSpaceId: string;
  dispatchFilters?: boolean;
  params: {
    sellerWorkspaceId: string;
  };
}

export interface FetchReturnOrderDetailsResponse {
  id: number;
  documentDate: string; // Assuming documentDate is always a string in ISO 8601 format.
  customerId: string;
  status: string;
  createdBy: string;
  createdByName: string;
  userType: string;
  subTotal: number;
  grossTotal: number;
  cfaId: string;
  cfaName: string;
  refOrderNumber: string;
  createdAt: string; // Assuming createdAt and updatedAt are always strings in ISO 8601 format.
  updatedAt: string;
  skucount: number;
  taxAmount: number;
  totalQuantity: number;
  importSource: string;
  lines: ReturnOrderLine[];
  invoiceLineSummary?: ReturnInvoiceLineSummary;
  batchNumberList: string[];
  statusList: string[];
  customerName: string;
  headDivisionCode?: string;
  headDivisionId?: string;
  headDivisionName?: string;
  erpOrderNumber?: string;
  custErpRONumber?: string;
  noOfBoxes?: number;
  goodsReceivedDate?: string;
  vehicleDetails?: string;
  lrNumber?: string;
  lrDate?: string;
  customerRefInvoiceNumber?: string;
  category?: string;
}

export interface ReturnInvoiceLineSummary {
  totalQty: number;
  totalAmount: string;
  invoicedItem: number;
  invoiceLines: any[];
}
export interface SubmitReturnRequest {
  sellerWorkspace: string;
  returnOrderId: number;
  status: string;
  customerId: string;
}

export interface DeleteReturnRequest {
  sellerWorkspace: string;
  customerId: string;
  cfaId: string;
  returnOrderId: number;
}

export interface UpdateReturnRequest {
  sellerWorkspaceId: string;
  customerId: string;
  status: string;
  returnOrderId: number;
  lines: UpdateReturnLine[];
  noOfBoxes?: number;
  goodsReceivedDate?: string;
  vehicleDetails?: string;
  lrNumber?: string;
  lrDate?: string;
  headDivisionId?: string;
  importSource?: string;
  category?: string;
}

export interface UpdateReturnLine {
  batchCode?: string;
  category: string;
  productVariantId?: number;
  quantity: number;
  returnType: string;
  status: string;
  returnOrderLineId?: number;
  batchId?: string;
  comments?: Comment[];
  invoiceNumber?: string;
}

export interface UploadReturnFileRequest {
  customerId: string;
  importSource: string;
  parserType: string;
  cfaId: string;
  headDivisionId?: string;
  category?: string;
  sellerWorkspaceId: string;
}

export interface UploadReturnFileResponse {
  id: number;
  documentDate: string;
  customerId: string;
  status: string;
  createdBy: string;
  createdByName: string;
  userType: string;
  subTotal: number;
  grossTotal: number;
  cfaId: string;
  cfaName: string;
  refOrderNumber: string;
  createdAt: string;
  updatedAt: string;
  skucount: number;
  taxAmount: number;
  totalQuantity: number;
  importSource: string;
  lines: ReturnOrderLine[];
  isTemplateError: boolean;
}

export interface UploadReturnFileLine {
  batchNumber: string;
  batchId: string;
  cfaType: any;
  expiryDate?: any;
  category: any;
  returnType: any;
  createdAt: string;
  updatedAt: string;
  quantity: number;
  erpOrderNumber: any;
  sku: any;
  status: string;
  returnOrderId: number;
  unitPrice: number;
  linePrice: number;
  id: number;
  productVariantId: number;
  productVariant: UploadReturnFileProductVariant;
  invoiceLineSummary: InvoiceLineSummary;
}

export interface UploadReturnFileProductVariant {
  id?: number;
  sku?: string;
  listPrice: number;
  name?: string;
}

export interface InvoiceLineSummary {
  totalQty: number;
  totalAmount: string;
  invoicedItem: number;
  invoiceLines: any[];
}

export interface DownloadReturnOrderBody {
  returnOrderId: string;
  customerId: string;
  includeProduct: boolean;
  includeInvoice: boolean;
  includeComments: boolean;
  downloadContentType: string;
  sortDirection: string;
}

export enum CategoryEnum {
  NotExpired = 'NE',
  Expired = 'E',
}

export enum AllReturnTypes {
  IncorrectProduct = 'IP',
  RejectedByPolicy = 'RBP',
  NotSold = 'NS',
  NotReceived = 'NR',
  Damaged = 'D',
  NonDamaged = 'ND',
  PurchaseReturn = 'PR',
  NearExpiry = 'NE',
}

export enum ReturnOrderLineStatusEnum {
  Accepted = 'ACCEPTED',
  Rejected = 'REJECTED',
}
